import { useMemo, useRef, useState } from "react";
import { datesInPeriod, shiftColor } from "./rosterUtils";
import { rosterTheme as t } from "./rosterTheme";

const ROW_HEIGHT = 36;
const COL_WIDTH = 88;
const VISIBLE_ROWS = 18;

export default function RosterCalendarPlanner({
  roster,
  assignments,
  employees,
  shifts,
  holidays,
  canEdit,
  onCellClick,
  onFillRange,
}) {
  const [scrollTop, setScrollTop] = useState(0);
  const dragRef = useRef(null);

  const dates = useMemo(
    () => (roster ? datesInPeriod(roster.period_start, roster.period_end) : []),
    [roster]
  );

  const holidaySet = useMemo(
    () => new Set((holidays || []).map((h) => String(h.holiday_date || h.date || "").slice(0, 10))),
    [holidays]
  );

  const safeAssignments = assignments || [];
  const safeEmployees = employees || [];
  const safeShifts = shifts || [];

  const shiftMap = useMemo(() => {
    const m = {};
    safeShifts.forEach((s) => {
      m[s.shift_id] = s;
    });
    return m;
  }, [safeShifts]);

  const byEmpDate = useMemo(() => {
    const m = {};
    safeAssignments.forEach((a) => {
      if (a.status === "deleted") return;
      m[`${a.employee_id}:${a.work_date}`] = a;
    });
    return m;
  }, [safeAssignments]);

  const empList = useMemo(() => {
    const ids = new Set(safeAssignments.map((a) => a.employee_id));
    const fromRows = safeEmployees.filter((e) => ids.has(e.employee_id));
    if (fromRows.length) return fromRows;
    const seen = new Map();
    safeAssignments.forEach((a) => {
      if (!seen.has(a.employee_id)) {
        seen.set(a.employee_id, {
          employee_id: a.employee_id,
          employee_code: a.employee_code,
          label: a.employee_name,
        });
      }
    });
    return [...seen.values()];
  }, [safeEmployees, safeAssignments]);

  const startRow = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - 2);
  const endRow = Math.min(empList.length, startRow + VISIBLE_ROWS + 4);
  const visibleEmps = empList.slice(startRow, endRow);

  const handleMouseDown = (empId, date) => {
    if (!canEdit) return;
    dragRef.current = { empId, start: date, end: date };
  };

  const handleMouseEnter = (empId, date) => {
    if (!dragRef.current || dragRef.current.empId !== empId) return;
    dragRef.current.end = date;
  };

  const handleMouseUp = () => {
    if (!dragRef.current || !onFillRange) {
      dragRef.current = null;
      return;
    }
    const { empId, start, end } = dragRef.current;
    const d0 = start <= end ? start : end;
    const d1 = start <= end ? end : start;
    onFillRange(empId, d0, d1);
    dragRef.current = null;
  };

  if (!roster) {
    return (
      <div className={`${t.card} border-dashed p-8 text-center text-slate-500 text-sm`}>
        Select a roster plan to open the monthly planner.
      </div>
    );
  }

  return (
    <div className={`${t.card} overflow-hidden`}>
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/80">
        <div className="flex flex-wrap justify-between items-center gap-2">
          <h3 className={t.cardHeader}>Monthly planner</h3>
          <span className="text-xs text-slate-500">Click cell to assign · drag to select range</span>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Visual grid of who works which shift each day in this roster period. Assignments from Quick
          assign or Bulk tools appear here.
        </p>
      </div>
      <div
        className="overflow-auto max-h-[520px]"
        onScroll={(e) => setScrollTop(e.target.scrollTop)}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <table className="border-collapse text-xs" style={{ minWidth: 220 + dates.length * COL_WIDTH }}>
          <thead className="sticky top-0 z-20 bg-gray-100">
            <tr>
              <th
                className="sticky left-0 z-30 bg-gray-100 border p-2 text-left min-w-[200px]"
                style={{ width: 200 }}
              >
                Employee
              </th>
              {dates.map((d) => {
                const day = new Date(d + "T00:00:00").getDay();
                const isOff = day === 0;
                const isHol = holidaySet.has(d);
                return (
                  <th
                    key={d}
                    className={`border p-1 text-center font-medium min-w-[88px] ${
                      isHol ? "bg-red-50 text-red-800" : isOff ? "bg-slate-50" : ""
                    }`}
                  >
                    <div>{d.slice(8)}</div>
                    <div className="text-[10px] text-gray-500">
                      {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"][day]}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {startRow > 0 && (
              <tr style={{ height: startRow * ROW_HEIGHT }}>
                <td colSpan={dates.length + 1} />
              </tr>
            )}
            {visibleEmps.map((emp) => (
              <tr key={emp.employee_id} style={{ height: ROW_HEIGHT }}>
                <td className="sticky left-0 z-10 bg-white border px-2 font-medium truncate max-w-[200px]">
                  {emp.label || emp.employee_name || emp.employee_code}
                </td>
                {dates.map((d) => {
                  const a = byEmpDate[`${emp.employee_id}:${d}`];
                  const sh = a ? shiftMap[a.shift_id] : null;
                  const isHol = holidaySet.has(d);
                  return (
                    <td
                      key={d}
                      className={`border p-0.5 align-middle cursor-pointer ${
                        isHol ? "bg-red-50/50" : ""
                      } ${canEdit ? "hover:ring-1 hover:ring-teal-400" : ""}`}
                      onClick={() => canEdit && onCellClick?.(emp.employee_id, d, a)}
                      onMouseDown={() => handleMouseDown(emp.employee_id, d)}
                      onMouseEnter={() => handleMouseEnter(emp.employee_id, d)}
                      title={
                        a
                          ? `${a.shift_name || sh?.name || "Shift"} · ${a.shift_timing || ""}`
                          : "Click to assign"
                      }
                    >
                      {a && sh ? (
                        <div
                          className={`rounded px-1 py-0.5 text-[10px] border truncate ${shiftColor(
                            sh.code
                          )}`}
                        >
                          {sh.name}
                        </div>
                      ) : (
                        <div className="h-6" />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {endRow < empList.length && (
              <tr style={{ height: (empList.length - endRow) * ROW_HEIGHT }}>
                <td colSpan={dates.length + 1} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {empList.length === 0 && (
        <p className="p-4 text-center text-gray-500 text-sm">Add assignments to populate the planner grid.</p>
      )}
    </div>
  );
}
